import axios from 'axios';
import { getDeviceId } from '../utils/deviceId';

/**
 * Obtiene la URL de reproducción de una película con la identificación del dispositivo
 * @param {string} movieId - ID de la película a reproducir
 * @returns {Promise} Resultado de la petición
 */
export async function getMoviePlaybackUrl(movieId) {
  try {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("No hay token de autenticación");

    const deviceId = getDeviceId();
    
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
    const response = await axios.get(
      `${apiUrl}/api/v1/movies/play/${movieId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'user-device-id': deviceId
        }
      }
    );
    
    return response.data;
  } catch (error) {
    // Comprobar si es un error 403 o 400 (posible error de límite de dispositivos)
    if (error.response && (error.response.status === 403 || error.response.status === 400)) {
      const errorMessage = error.response.data?.error || 'No se puede reproducir en este dispositivo.';
      alert(errorMessage);
    }
    throw error;
  }
}

/**
 * Envía un evento de reproducción al servidor para seguimiento de tiempo de visualización
 * @param {string} contentId - ID de la película o episodio que se está reproduciendo
 * @param {string} eventType - Tipo de evento (start, pause, resume, seek, end)
 * @param {number} position - Posición actual en segundos
 * @param {number} totalDuration - Duración total del video en segundos
 * @param {string} contentType - Tipo de contenido ('movie' o 'episode')
 * @returns {Promise} Resultado de la petición
 */
export async function trackPlaybackEvent(contentId, eventType, position, totalDuration, contentType = 'movie') {
  try {
    // Para episodios, simplemente registramos en consola pero no enviamos al backend
    // hasta que se implemente la funcionalidad de tracking para episodios
    if (contentType === 'episode') {
      console.log(`Seguimiento de episodio (no implementado en backend): ID=${contentId}, evento=${eventType}, posición=${position}s, duración=${totalDuration}s`);
      return { success: true, message: "Seguimiento local (no enviado al backend)" };
    }

    const token = localStorage.getItem("token");
    if (!token) throw new Error("No hay token de autenticación");

    const deviceId = getDeviceId();
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
    
    // Calculamos el porcentaje visto
    const percentageWatched = totalDuration > 0 ? Math.round((position / totalDuration) * 100) : 0;
    
    // Verificamos si el usuario ha completado la película (más del 90% visto)
    const completed = percentageWatched >= 90;
    
    // Usar el endpoint de movie-views para películas (el único implementado actualmente)
    const endpoint = `${apiUrl}/api/v1/movie-views/track-event`;
    
    const payload = {
      movieId: contentId,
      eventType,
      position,
      totalDuration,
      percentageWatched,
      completed
    };
    
    console.log(`Registrando evento de reproducción para película:`, payload);
    
    const response = await axios.post(
      endpoint,
      payload,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'user-device-id': deviceId
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error(`Error al registrar evento de reproducción de ${contentType}:`, error);
    // No interrumpimos la experiencia del usuario si falla el tracking
    return null;
  }
}

/**
 * Obtiene el historial de visualización del usuario actual
 * @returns {Promise} Historial de visualización del usuario
 */
export async function getUserViewingHistory() {
  try {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("No hay token de autenticación");
    
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
    const response = await axios.get(
      `${apiUrl}/api/v1/movie-views/my-history`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error("Error al obtener historial de visualización:", error);
    throw error;
  }
}
